import { z } from 'zod';
import { BusinessGovernance } from './business-context.js';
/**
 * CompanyContext — per-org "company profile" snapshot that backs the v7
 * `/profile/company` surface (epic #123, Phase 3). Distinct from the
 * onboarding-time `BusinessContext` capture which models industry +
 * stage + size + goals + signal topics. This doc layers in the
 * **operating rhythm** the team runs on (briefing time, weekly L10
 * cadence, quarterly Rocks kickoff offset), the structured **market
 * position** that the agent learns from (industry tags, customer
 * profile, ICP), the **funding history** snapshot (latest round, lead
 * investor, runway), and the **engineering org** shape (team size,
 * languages, repo count).
 *
 * Why a separate doc instead of growing `BusinessContext`:
 *   - Different write cadence. `BusinessContext` is mostly write-once
 *     during onboarding; this doc accumulates as the agent learns more
 *     facts week-over-week (the AI-derived "What we learned" rows live
 *     alongside the operator-told fields).
 *   - Different surface boundary. The personal `/profile` page renders
 *     `users/{uid}` fields + per-user learned facts; the company
 *     `/profile/company` page renders `companyContexts/{orgId}` +
 *     per-org learned facts. Keeping the docs separate lets the rules
 *     layer gate them independently (per-user vs orgAdmin-write).
 *   - Reuses the existing `BusinessGovernance` (formation state, fiscal
 *     year, board cadence) so a value typed during agent-led onboarding
 *     surfaces here without a remap layer.
 *
 * Lives at `companyContexts/{orgId}` (composite `id === orgId` by
 * convention so the lookup is a single get without a where-query).
 * Multi-tenant: `orgId` on every doc; reads gated by `isOrgMember`,
 * writes by `isOrgAdmin` in the rules layer.
 */
export const COMPANY_CONTEXTS_COLLECTION = 'companyContexts';
/**
 * Operating rituals — when the team meets and on what cadence. Drives:
 *   - The Today briefing's "what's next this week" callout (which day
 *     the L10 lands, what time the briefing should be ready by).
 *   - The L10-prep generator's "when to fire" cron (run at `l10Time -
 *     90min` on `l10DayOfWeek`).
 *   - The Rocks kickoff reminder (`rocksKickoffWeek` of each quarter).
 *
 * ISO weekday numbering — `1 = Monday … 7 = Sunday`. Times stamped in
 * 24h `HH:MM` so the regex is straightforward to validate. Stored in
 * the org's local timezone interpretation; per-org timezone preference
 * is captured separately on `users/{uid}.timezone` (and a future
 * org-level pref).
 */
export const OperatingRituals = z.object({
    /** Day-of-week the Monday-morning briefing is due. ISO weekday (1=Mon..7=Sun). */
    briefingDayOfWeek: z.number().int().min(1).max(7).default(1),
    /** 24h `HH:MM` time the briefing is delivered (org-local). */
    briefingTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be 24h HH:MM (e.g. "07:30")')
        .default('07:30'),
    /** Day-of-week the weekly L10 (or equivalent) runs. */
    l10DayOfWeek: z.number().int().min(1).max(7).default(1),
    /** 24h `HH:MM` time the L10 kicks off (org-local). */
    l10Time: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be 24h HH:MM (e.g. "09:30")')
        .default('09:30'),
    /** Whether the team uses EOS L10 (`true`) or an alternate weekly cadence. */
    usesEosL10: z.boolean().default(true),
    /** Which week of the quarter the Rocks-planning kickoff lands (1-13). */
    rocksKickoffWeek: z.number().int().min(1).max(13).default(2),
});
/**
 * Market position — who the company sells to and what space they play
 * in. Distinct from `BusinessContext.companyDescription` (the one-line
 * "what we do"); this is the structured ICP that the agent uses to
 * filter signals + reason about competitive context.
 *
 * Free-text `customerProfile` + `idealCustomerProfile` because the
 * shape of "who we serve" varies wildly across orgs — a recruiting
 * firm's ICP is a hiring-manager persona, a fintech's is a regulated
 * counterparty. Captured here in narrative form; the learned-fact path
 * can refine these with enrichment over time.
 */
export const MarketPosition = z.object({
    /** Short hyphenated tags ("vertical-ai", "developer-tools"). Max 6. */
    industryTags: z.array(z.string().min(1).max(40)).max(6).default([]),
    /** Free-text "who buys today" — current customer description. */
    customerProfile: z.string().max(2000).default(''),
    /** Free-text ICP description — who the team is trying to win next. */
    idealCustomerProfile: z.string().max(2000).default(''),
});
/**
 * Funding history snapshot. Phase 3 captures the operator-stated
 * version; future learned-fact runs can pull Crunchbase / PitchBook
 * + corroborate the human number against public-record filings.
 *
 * `latestRoundLabel` is free-text ("Series B", "SAFE — angel", "Bridge
 * note") to cover the long tail without an enum that won't keep up.
 * `latestRoundUsd` + `runwayMonths` are numeric so the agent can reason
 * about scale + urgency. `latestRoundClosedAt` is an optional ISO date.
 */
export const FundingHistory = z.object({
    latestRoundLabel: z.string().max(40).default(''),
    latestRoundUsd: z.number().nonnegative().optional(),
    leadInvestor: z.string().max(120).default(''),
    runwayMonths: z.number().nonnegative().optional(),
    /** ISO-8601 date string of the latest round close, if known. */
    latestRoundClosedAt: z.string().optional(),
});
/**
 * Engineering org snapshot — team size + primary languages + repo
 * count. Drives the agent's reasoning about deploy cadence + hiring
 * signals + tool-fit recommendations. Captured as the operator-stated
 * baseline; the GitHub connector's learned-fact pass refines these
 * numbers once it's wired.
 */
export const EngineeringOrg = z.object({
    teamSize: z.number().nonnegative().default(0),
    primaryLanguages: z.array(z.string().min(1).max(40)).max(8).default([]),
    repoCount: z.number().nonnegative().default(0),
    monorepo: z.boolean().default(false),
});
/**
 * Full `companyContexts/{orgId}` document shape. Each nested block
 * carries its own defaults so a partial first-write (admin clicks
 * "Edit" on a never-populated section) parses cleanly. Multi-tenant
 * invariant: `orgId` mirrors the doc id; the store enforces this on
 * every read/write.
 */
export const CompanyContext = z.object({
    orgId: z.string().min(1),
    governance: BusinessGovernance,
    rituals: OperatingRituals,
    market: MarketPosition,
    funding: FundingHistory,
    engineering: EngineeringOrg,
    /** ISO timestamp of the last successful upsert. */
    updatedAt: z.string(),
    /** uid of the operator who last updated this doc. */
    updatedByUid: z.string().min(1),
});
/**
 * Patch shapes — every field optional and every default stripped, so a
 * single-field PUT body parses to a single-field patch (not a fully-
 * defaulted snapshot that would clobber every other operator-set value
 * during the store-layer merge). Defined inline rather than as
 * `OperatingRituals.partial()` because Zod's `.partial()` keeps the
 * per-field `.default(...)` modifier, which would fill missing fields
 * with their seed values on parse — wrong for a patch.
 */
const RitualsPatch = z.object({
    briefingDayOfWeek: z.number().int().min(1).max(7).optional(),
    briefingTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be 24h HH:MM (e.g. "07:30")')
        .optional(),
    l10DayOfWeek: z.number().int().min(1).max(7).optional(),
    l10Time: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be 24h HH:MM (e.g. "09:30")')
        .optional(),
    usesEosL10: z.boolean().optional(),
    rocksKickoffWeek: z.number().int().min(1).max(13).optional(),
});
const MarketPatch = z.object({
    industryTags: z.array(z.string().min(1).max(40)).max(6).optional(),
    customerProfile: z.string().max(2000).optional(),
    idealCustomerProfile: z.string().max(2000).optional(),
});
/**
 * Numeric patch fields accept `number | null` — `null` is the wire
 * signal to clear an existing value (mapped to `FieldValue.delete()`
 * at the store layer). Plain `undefined` would be dropped by
 * `JSON.stringify` at the client and arrive as "field omitted" rather
 * than "clear this field," so we model the two intentions explicitly.
 */
const NullableNonNegativeNumber = z.number().nonnegative().nullable().optional();
const FundingPatch = z.object({
    latestRoundLabel: z.string().max(40).optional(),
    latestRoundUsd: NullableNonNegativeNumber,
    leadInvestor: z.string().max(120).optional(),
    runwayMonths: NullableNonNegativeNumber,
    latestRoundClosedAt: z.string().optional(),
});
const EngineeringPatch = z.object({
    teamSize: NullableNonNegativeNumber,
    primaryLanguages: z.array(z.string().min(1).max(40)).max(8).optional(),
    repoCount: NullableNonNegativeNumber,
    monorepo: z.boolean().optional(),
});
/**
 * Body for `PUT /api/orgs/:o/company-profile`. Every nested section is
 * a `Patch` shape (every field optional, no defaults applied on parse)
 * so an admin can update one block at a time without re-sending the
 * whole snapshot.
 *
 * Cross-section refinements (if any) belong at the store layer, not
 * here — keep the wire shape additive + permissive so a future field
 * doesn't require a coordinated client+server release.
 */
export const UpdateCompanyContextRequest = z.object({
    governance: BusinessGovernance.partial().optional(),
    rituals: RitualsPatch.optional(),
    market: MarketPatch.optional(),
    funding: FundingPatch.optional(),
    engineering: EngineeringPatch.optional(),
});
